import { NavLink, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store/auth.js";
import type { MyTenant } from "@/lib/api.js";

function safeJson(s: string): Record<string, string> {
  try { return JSON.parse(s) as Record<string, string>; } catch { return {}; }
}

const NAV_ITEMS = [
  { path: "dashboard", label: "대시보드", icon: "◈" },
  { path: "main-pages", label: "메인페이지", icon: "⊞" },
  { path: "tests", label: "검사", icon: "🎯" },
  { path: "surveys", label: "설문", icon: "📋" },
  { path: "stats", label: "통계", icon: "▦" },
  { path: "responses", label: "응답자", icon: "◐" },
  { path: "theme", label: "테마", icon: "🎨" },
];

interface Props {
  myTenants: MyTenant[];
  activeTenantSlug: string;
}

export function Sidebar({ myTenants, activeTenantSlug }: Props) {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <aside className="flex h-screen w-56 shrink-0 flex-col border-r border-gray-200 bg-white">
      <div className="px-5 py-5">
        <span className="text-sm font-bold text-brand-600">사찰관리자</span>
      </div>

      {/* 사찰 목록 */}
      {myTenants.length > 0 && (
        <div className="px-2 pb-2">
          <p className="mb-1 px-2 text-xs font-medium text-gray-400">사찰</p>
          {myTenants.map((t) => {
            const name = safeJson(t.name).ko || t.slug;
            const isActive = t.slug === activeTenantSlug;
            return (
              <NavLink
                key={t.id}
                to={`/${t.slug}/dashboard`}
                className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? "bg-brand-50 font-semibold text-brand-700"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                }`}
              >
                <span className="text-base">⛩</span>
                <span className="truncate">{name}</span>
              </NavLink>
            );
          })}
        </div>
      )}

      {/* 현재 사찰 네비게이션 */}
      {activeTenantSlug && (
        <>
          <div className="mx-3 my-1 border-t border-gray-100" />
          <nav className="flex-1 overflow-y-auto px-2 py-1">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.path}
                to={`/${activeTenantSlug}/${item.path}`}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                    isActive
                      ? "bg-brand-50 font-semibold text-brand-700"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  }`
                }
              >
                <span className="text-base">{item.icon}</span>
                {item.label}
              </NavLink>
            ))}
          </nav>
        </>
      )}

      {!activeTenantSlug && <div className="flex-1" />}

      <div className="border-t border-gray-100 px-4 py-4">
        <p className="truncate text-xs text-gray-400">{user?.email}</p>
        <button
          onClick={handleLogout}
          className="mt-2 w-full rounded-lg px-3 py-2 text-left text-sm text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700"
        >
          로그아웃
        </button>
      </div>
    </aside>
  );
}
